import React from 'react'

import {
  splitByLineBreakOrComma,
  truncateKeyword,
} from '../../../utils/functions'
import { getLanguageCode, translate } from '../../../utils/translation'
import Icon from '../../icon/Icon'
import enterIcon from '../../../styles/assets/dashboard/enter_icon.svg'
import { Text } from '../../topography'
import { palette } from '../../../styles/variables'

import { Tag } from './Tag'
import {
  Description,
  ReactTagInputInput,
  ReactTagInputWrapper,
  Wrapper,
  Placeholder,
} from './styled'

export default class KeywordTagInput extends React.Component {
  constructor(props) {
    super(props)
    this.inputRef = React.createRef()
    this.state = { input: props.input || '', shouldShowPlaceholder: true }
  }

  onInputChange = (e) => {
    const input = e.target.value
      .toLowerCase()
      .split('')
      .filter(
        (val1, index, arr) =>
          index === 0 || val1 !== ' ' || arr[index - 1] !== ' ',
      )
      .join('')

    if (this.props.onInputChange) {
      this.props.onInputChange(input)
    }
    this.setState((previousState) => ({ ...previousState, input }))
  }

  onInputKeyDown = (e) => {
    const { input } = this.state
    const { tags, validator, removeOnBackspace } = this.props

    // On enter
    if (e.keyCode === 13 || e.keyCode === 188) {
      // Prevent form submission if tag input is nested in <form>
      e.preventDefault()

      // If input is blank, do nothing
      if (input === '') {
        return
      }

      // Check if input is valid
      const valid = validator !== undefined ? validator(input) : true
      if (!valid) {
        return
      }

      // Check if input is already existing
      if (tags.some((t) => t === truncateKeyword(input))) {
        return
      }

      // Add input to tag list
      this.addTag(input)
    }
    // On backspace or delete
    else if (removeOnBackspace && (e.keyCode === 8 || e.keyCode === 46)) {
      // If currently typing, do nothing
      if (input !== '') {
        return
      }

      // If input is blank, remove previous tag
      this.removeTag(this.props.tags.length - 1)
    }
  }

  addTag = (value) => {
    const tags = [...this.props.tags]
    tags.push(truncateKeyword(value))
    this.props.onChange(tags)
    this.setState((previousState) => ({ ...previousState, input: '' }))
  }

  removeTag = (i) => {
    const tags = [...this.props.tags]
    tags.splice(i, 1)
    this.props.onChange(tags)
  }

  updateTag = (i, value) => {
    const tags = [...this.props.tags]
    tags[i] = value
    this.props.onChange(tags)
  }

  onPaste = (e) => {
    // Cancel paste event
    e.preventDefault()

    // Remove formatting from clipboard contents
    const text = e.clipboardData.getData('text/plain')
    const newTags = splitByLineBreakOrComma(text).map((k) => truncateKeyword(k))
    const tags = [...this.props.tags].concat(newTags)

    this.props.onChange(tags)
    this.setState((previousState) => ({ ...previousState, input: '' }))
  }

  hideOrShowPlaceholder = (eventType) => {
    const shouldShowPlaceholder = !this.state.input && eventType !== 'focus'
    this.setState((previousState) => ({
      ...previousState,
      shouldShowPlaceholder,
    }))
  }

  render() {
    const { input, shouldShowPlaceholder } = this.state

    const {
      initialCount,
      tags,
      tagWeight = 1,
      placeholder,
      maxTags,
      editable,
      readOnly,
      validator,
      removeOnBackspace,
      limitErrorMessageId,
      descriptionMessageId,
      height,
    } = this.props

    const currentCount = initialCount + tags.length * tagWeight
    const maxTagsReached =
      maxTags !== undefined ? currentCount > maxTags : false
    const isEditable = readOnly ? false : editable || false
    const inputWidth =
      tags.length === 0 && !input ? '100%' : `${input.length + 1}ch`
    const wrapperWidth =
      getLanguageCode() === 'de'
        ? `calc(${inputWidth} + 95px)`
        : `calc(${inputWidth} + 50px)`
    return (
      <>
        <Wrapper
          data-testid="keyword-tag-input-wrap"
          onClick={() => this.inputRef.current.focus()}
          maxTagsReached={maxTagsReached}
          height={height}
        >
          <ReactTagInputWrapper>
            {tags.map((tag, i) => (
              <Tag
                key={i}
                testId="keyword-tag-input-item"
                value={tag}
                index={i}
                editable={isEditable}
                readOnly={readOnly || false}
                inputRef={this.inputRef}
                update={this.updateTag}
                remove={this.removeTag}
                validator={validator}
                removeOnBackspace={removeOnBackspace}
                invalidTag={initialCount + (i + 1) * tagWeight > maxTags}
              />
            ))}
            <div
              style={{
                display: 'flex',
                width: wrapperWidth,
              }}
            >
              <ReactTagInputInput
                ref={this.inputRef}
                value={input}
                onChange={this.onInputChange}
                onKeyDown={this.onInputKeyDown}
                onPaste={this.onPaste}
                maxLength="100"
                width={inputWidth}
                onFocus={() => this.hideOrShowPlaceholder('focus')}
                onBlur={this.hideOrShowPlaceholder}
              />
              {!!input && (
                <Text
                  color={palette.gray}
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <Icon src={enterIcon} margin="0 4px 0 0" />
                  {translate('hit_enter')}
                </Text>
              )}
            </div>
            {shouldShowPlaceholder && <Placeholder>{placeholder}</Placeholder>}
          </ReactTagInputWrapper>
        </Wrapper>
        {maxTagsReached && (
          <Description color={palette.red}>
            {translate(limitErrorMessageId, maxTags)}
          </Description>
        )}
        {descriptionMessageId && (
          <Description data-testid="keyword-tag-input-description">
            {translate(
              descriptionMessageId,
              Math.max(0, maxTags - currentCount),
            )}
            <span>
              <span
                data-testid="keyword-tag-input-count"
                style={{ color: maxTagsReached ? palette.red : 'inherit' }}
              >
                {currentCount}
              </span>
              /{maxTags}
            </span>
          </Description>
        )}
      </>
    )
  }
}
